import { Form, Switch, Divider, DatePicker, Select, InputNumber, Flex } from "antd";
import dayjs from "dayjs";
import { Calendar03Icon, ChevronDownIcon } from "@/icons";
import { disablePastDates } from "@/utils/format/date";
import {
  RECOMMENDATION_SECTIONS,
  type RecommendationSection,
  getRecommendationSections,
  isRecommendationSectionDisabled,
  toggleRecommendationSection,
} from "./recommendation.constants";
import SetupAppraisalBenefit from "./SetupAppraisalBenefit";

interface SetupRecomendationProps {
  designations?: { id: string; name: string }[];
  isDesignationLoading?: boolean;
}

const SetupRecomendation = ({
  designations = [],
  isDesignationLoading = false,
}: SetupRecomendationProps) => {
  const form = Form.useFormInstance();
  const recommendationType = Form.useWatch("recommendation_type", form);
  const activeSections = getRecommendationSections(recommendationType);

  const toggleSection = (sectionKey: RecommendationSection) => {
    const currentVal = form.getFieldValue("recommendation_type");
    form.setFieldValue(
      "recommendation_type",
      toggleRecommendationSection(currentVal, sectionKey),
    );
  };

  return (
    <div className="border border-theme-gray-700 rounded-xl p-6 space-y-0 mt-6">
      {/* Registers the field so its (possibly combined) value is submitted. */}
      <Form.Item name="recommendation_type" hidden />

      {RECOMMENDATION_SECTIONS.map((section, sIdx) => {
        const isActive = activeSections.includes(section.key);
        const isDisabled = isRecommendationSectionDisabled(
          recommendationType,
          section.key,
        );
        const isLast = sIdx === RECOMMENDATION_SECTIONS.length - 1;

        return (
          <div key={section.key}>
            {/* Section Header */}
            <Flex justify="space-between" align="center">
              <h5 className="text-lg font-medium text-white">
                {section.label}
              </h5>
              <Switch
                className="custom-switch"
                checked={isActive}
                disabled={isDisabled}
                onChange={() => toggleSection(section.key)}
              />
            </Flex>

            {/* Section Body */}
            {isActive && (
              <>
                <Divider className="border-dashed my-3" />

                {/* ── Dismissal ── */}
                {section.key === "DISMISSAL" && (
                  <Form.Item
                    label="Last Working Day"
                    name="last_working_day"
                    rules={[
                      {
                        required: true,
                        message: "Last working day is required",
                      },
                    ]}
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      placeholder="dd/mm/yyyy"
                      suffixIcon={
                        <Calendar03Icon
                          size={20}
                          className="text-white"
                        />
                      }
                      className="bg-theme-slate-900 border-theme-gray-800"
                      disabledDate={(date) => disablePastDates(date)}
                    />
                  </Form.Item>
                )}

                {/* ── Incentive ── */}
                {section.key === "INCENTIVE" && (
                  <>
                    <Form.Item
                      label="Effective Month"
                      name="incentive_effective_month"
                      rules={[
                        {
                          required: true,
                          message: "Effective month is required",
                        },
                      ]}
                    >
                      <DatePicker
                        picker="month"
                        format="MMMM YYYY"
                        placeholder="mm/yyyy"
                        suffixIcon={
                          <Calendar03Icon
                            size={20}
                            className="text-white"
                          />
                        }
                        className="bg-theme-slate-900 border-theme-gray-800"
                        disabledDate={(date) =>
                          date.isBefore(dayjs().endOf("month"))
                        }
                      />
                    </Form.Item>
                    <SetupAppraisalBenefit prefix="incentive" />
                  </>
                )}

                {/* ── Promotion ── */}
                {section.key === "PROMOTION" && (
                  <div className="space-y-4">
                    <Form.Item
                      label="Suggested Designation"
                      name="suggested_designation_id"
                      rules={[
                        {
                          required: true,
                          message: "Suggested designation is required",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select"
                        loading={isDesignationLoading}
                        options={designations.map(
                          (item: any) => ({
                            value: item.id,
                            label: item.name,
                          }),
                        )}
                        suffixIcon={
                          <ChevronDownIcon
                            size={20}
                            className="text-white"
                          />
                        }
                      />
                    </Form.Item>
                    <div className="grid grid-cols-2 gap-6">
                      <Form.Item
                        label="New Salary"
                        name="new_salary"
                        rules={[
                          {
                            required: true,
                            message: "New salary is required",
                          },
                          {
                            validator: (_, value) => {
                              if (value && isNaN(Number(value)))
                                return Promise.reject(
                                  new Error("Enter numbers only"),
                                );
                              if (value && Number(value) < 1)
                                return Promise.reject(
                                  new Error("Min value is 1"),
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="Enter new salary"
                          className="w-full"
                          controls={false}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Effective Month"
                        name="promotion_effective_month"
                        rules={[
                          {
                            required: true,
                            message: "Effective month is required",
                          },
                        ]}
                      >
                        <DatePicker
                          picker="month"
                          format="MMMM YYYY"
                          placeholder="mm/yyyy"
                          suffixIcon={
                            <Calendar03Icon
                              size={20}
                              className="text-white"
                            />
                          }
                          className="bg-theme-slate-900 border-theme-gray-800 w-full"
                          disabledDate={(date) =>
                            date.isBefore(dayjs().endOf("month"))
                          }
                        />
                      </Form.Item>
                    </div>
                  </div>
                )}

                {/* ── Increment ── */}
                {section.key === "INCREMENT" && (
                  <>
                    <Form.Item
                      label="Effective Month"
                      name="increment_effective_month"
                      rules={[
                        {
                          required: true,
                          message: "Effective month is required",
                        },
                      ]}
                    >
                      <DatePicker
                        picker="month"
                        format="MMMM YYYY"
                        placeholder="mm/yyyy"
                        suffixIcon={
                          <Calendar03Icon
                            size={20}
                            className="text-white"
                          />
                        }
                        className="bg-theme-slate-900 border-theme-gray-800"
                        disabledDate={(date) =>
                          date.isBefore(dayjs().endOf("month"))
                        }
                      />
                    </Form.Item>
                    <SetupAppraisalBenefit prefix="increment" />
                  </>
                )}
              </>
            )}

            {/* Divider between sections (not after last) */}
            {!isLast && <Divider className="my-5 bg-white/10" />}
          </div>
        );
      })}
    </div>
  );
};

export default SetupRecomendation;
